// --- React & Styles
import React from 'react';
// --- Redux
import {useSelector} from 'react-redux';
// --- UI Components
import {Col, Row} from 'antd';
import {InputLabel} from '../../../InputLabel/InputLabel';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import Typography from '@dtci/spellbook/Typography';
// --- Utilities
import moment from 'moment';
import {filterCriteria} from '../../../../utils/filterCriteria';
// --- Hooks
import {useCommentsActions} from '../../../../store/comments/useComments';
import {useUploadDialog} from '../../../dialogs/dialogs/upload';
import {useLoading} from '../../../../utils/hooks/useLoading';
import {useDeleteDialog} from '../../../dialogs/dialogs/delete';
import {useToastNotification} from '../../../ToastNotifications';
// --- API
import {datastageApi} from '../../../../api';
// -- Styles
import './styles.scss';
import {startJobStatusTracker} from '../../../../utils/jobStatusTracker';
import {usePermissions} from '../../../../utils/permissions';

/**
 * AttachmentsForm Component
 *
 * This component is responsible for rendering the attachments form in the comments modal.
 * It allows users to upload and delete attachments related to a comment.
 * It also displays the comment title, created by, and created date.
 *
 * @param {Object} comment - The comment object containing details about the comment.
 * @returns {JSX.Element} The rendered AttachmentsForm component.
 */
export function AttachmentsForm({comment}) {
    const {openUploadDialog} = useUploadDialog();
    const {openDeleteDialog} = useDeleteDialog();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {updateCommentsFilterForm, updateComments} = useCommentsActions();
    const {openToast} = useToastNotification();
    const {filterForm, commentProduct, commentRange, commentSort} = useSelector((s) => s.comments);
    const {isLegalAdmin, isProductCommentsAdmin} = usePermissions();

    // Function to handle the upload dialog
    const onOpenUploadDialog = (data) => {
        openUploadDialog(data, async ({file}) => {
            if (!file) return;

            const formData = new FormData();
            file.forEach((rawFile) => formData.append('files', rawFile));
            formData.append('productCommentId', comment.id);

            try {
                setLoadingTrue();

                const res = await datastageApi.uploadAttachment(formData);
                startJobStatusTracker(res?.id, async (message, status) => {
                    openToast(message, status);

                    if (status === 'success') {
                        const commentList = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort,
                        );
                        const openComment = await datastageApi.commentById(comment.id);

                        updateComments({
                            comments: commentList?.content,
                            comment: openComment,
                            isAttachmentsModalOpen: status === 'success' && false,
                            isCreateModalOpen: false,
                            isEditModalOpen: true,
                        });
                    }

                    setLoadingFalse();
                });

                setLoadingFalse();
            } catch (error) {
                console.error('Error uploading attachment:', error);
                openToast('Error uploading attachment', 'error');
                setLoadingFalse();
            }
        });
    };

    // Function to handle the delete dialog
    const onOpenDeleteDialog = (data) => {
        openDeleteDialog(
            'Yes, Delete',
            [
                `Are you sure you want to delete ${data?.attachmentName}`,
                'Please note: This action can not be reversed.',
            ],
            async () => {
                setLoadingTrue();
                try {
                    const res = await datastageApi.deleteAttachment(data.id, false);
                    if (res?.success === true) {
                        openToast('Attachment deleted successfully', 'success');
                        const commentList = await datastageApi.comments(
                            filterCriteria(filterForm, commentProduct, 'comments'),
                            commentRange,
                            commentSort,
                        );
                        const openComment = await datastageApi.commentById(data.productCommentId);
                        updateComments({
                            comments: commentList?.content,
                            comment: openComment,
                            isAttachmentsModalOpen: false,
                            isCreateModalOpen: false,
                            isEditModalOpen: true,
                        });
                    } else {
                        openToast(
                            `${
                                (res?.statusText && res?.statusText) ||
                                'Failed to delete attachment'
                            }`,
                            'error',
                        );
                    }
                } catch (error) {
                    console.error('Error deleting attachment:', error);
                    openToast('Error deleting attachment', 'error');
                } finally {
                    setLoadingFalse();
                }
            }
        );
    };


    const downloadAttachment = async (rightsLineId, fileName) => {
        setLoadingTrue();
        const res = await datastageApi.downloadAttachment(rightsLineId, fileName);
        saveAs(res.data, fileName);
        setLoadingFalse();
    };

    return (
        <>
            <div className="attachment-form">
                <div className="attachment-title">
                    <InputLabel label="Comment Title">
                        <Input
                            value={comment.title}
                            disabled={!isLegalAdmin && !isProductCommentsAdmin}
                            width="auto"
                            onChange={(e) =>
                                updateCommentsFilterForm({
                                    key: 'comment',
                                    data: {title: e.target.value},
                                })
                            }
                            id="title"
                            name="title"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </div>
                <div className="attachment-meta">
                    <Typography variant="T30">{comment?.createdBy?.fullName}</Typography>
                </div>
                <div className="attachment-meta">
                    <Typography variant="T30">
                        {moment(comment?.createdAt).format('YYYY-MM-DD')}
                    </Typography>
                </div>
                {(isLegalAdmin || isProductCommentsAdmin) && (
                    <div className="attachment-upload">
                        <Button
                            iconType="fa-regular fa-upload"
                            sizeType="large"
                            styleType="link"
                            onClick={onOpenUploadDialog}
                        >
                            Upload Attachments
                        </Button>
                    </div>
                )}
            </div>

            <div className="attachment-list">
                {comment?.attachments.map((doc) => (
                    <div key={doc.id || doc.attachmentName} className="attachment-item">
                        <a
                            className="attachment-link"
                            onClick={(e) => {
                                e.preventDefault();
                                downloadAttachment(doc?.rightslineId, doc?.attachmentName)
                            }}
                        >
                            {doc.attachmentName}
                        </a>
                        {(isLegalAdmin || isProductCommentsAdmin) && (
                            <Button
                                iconType="fa-regular fa-trash"
                                sizeType="large"
                                styleType="link"
                                onClick={() => onOpenDeleteDialog(doc)}
                            />
                        )}
                    </div>
                ))}
            </div>
        </>
    );
}